import React, { useCallback, useEffect, useState } from 'react';
import { Building2, Plus, Pencil, Trash2, Loader2, X, Check } from 'lucide-react';
import { toast } from 'sonner';
import { CommanServices } from '@/services/commanServices';

const ASSET_TYPES = [
  'hospital', 'power', 'water', 'communications', 'transport',
  'warehouse', 'school', 'government', 'other',
];
const CRITICALITY_STYLE = {
  5: 'bg-red-100 text-red-700 border-red-200',
  4: 'bg-rose-50 text-rose-700 border-rose-200',
  3: 'bg-orange-50 text-orange-700 border-orange-200',
  2: 'bg-yellow-50 text-yellow-700 border-yellow-200',
  1: 'bg-gray-50 text-gray-600 border-gray-200',
};
const blank = () => ({ name: '', asset_type: 'hospital', criticality: 3, zone_id: '', lat: '', lng: '', is_active: true });

// Critical-asset registry: the fixed facilities the risk engine checks a
// warning's impact area against (the "Assets" factor + "Possibly affected").
const CriticalAssetsCard = () => {
  const [assets, setAssets] = useState([]);
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null); // null | {id?, ...blank()}
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [assetsRes, zonesRes] = await Promise.all([
        CommanServices.getAssets(),
        CommanServices.getlocationszonesdata().catch(() => null),
      ]);
      const data = assetsRes?.data || assetsRes;
      if (data?.assets) setAssets(data.assets);
      const zonesData = zonesRes?.data || zonesRes;
      const list = Array.isArray(zonesData) ? zonesData : (zonesData?.zones || zonesData?.locations || []);
      setZones(Array.isArray(list) ? list : []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (saving) return;
    if (!form.name.trim()) { toast.error('Asset name is required'); return; }
    const hasGps = form.lat !== '' && form.lng !== '';
    if (!hasGps && !form.zone_id) { toast.error('Give the asset GPS coordinates or a zone'); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        asset_type: form.asset_type,
        criticality: Number(form.criticality),
        zone_id: form.zone_id || null,
        gps: hasGps ? { latitude: Number(form.lat), longitude: Number(form.lng) } : null,
        is_active: form.is_active,
      };
      const res = form.id
        ? await CommanServices.updateAsset(form.id, payload)
        : await CommanServices.createAsset(payload);
      if (res?.status === false) { toast.error(res?.message || 'Save failed'); return; }
      toast.success(form.id ? 'Asset updated' : 'Asset added');
      setForm(null);
      load();
    } finally {
      setSaving(false);
    }
  };

  const remove = async (asset) => {
    if (!window.confirm(`Delete asset "${asset.name}"?`)) return;
    const res = await CommanServices.deleteAsset(asset.id);
    if (res?.status === false) { toast.error(res?.message || 'Delete failed'); return; }
    toast.success('Asset deleted');
    load();
  };

  const edit = (a) => setForm({
    id: a.id, name: a.name, asset_type: a.asset_type || 'other',
    criticality: a.criticality || 3, zone_id: a.zone_id || '',
    lat: a.gps?.latitude ?? '', lng: a.gps?.longitude ?? '',
    is_active: a.is_active !== false,
  });

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 mb-4" data-testid="critical-assets-card">
      <div className="flex items-center justify-between gap-2 mb-1">
        <p className="text-sm font-bold text-gray-800 flex items-center gap-1.5">
          <Building2 className="h-4 w-4 text-violet-600" /> Critical Assets
          <span className="text-[11px] font-semibold text-gray-400">{assets.length} registered</span>
        </p>
        {!form && (
          <button onClick={() => setForm(blank())} data-testid="asset-add"
            className="flex items-center gap-1 rounded-md border border-violet-200 bg-violet-50 text-violet-700 text-[11px] font-bold px-2 py-1 hover:bg-violet-100">
            <Plus className="h-3 w-3" /> Add asset
          </button>
        )}
      </div>
      <p className="text-[11px] text-gray-500 mb-3">
        Facilities the ForeSight risk engine checks against every warning's impact area — criticality 5 weighs highest.
      </p>

      {form && (
        <div className="rounded-lg border border-violet-100 bg-violet-50/40 p-3 mb-3 grid grid-cols-2 md:grid-cols-6 gap-2 items-end" data-testid="asset-form">
          <label className="col-span-2 text-[11px] font-semibold text-gray-600">Name
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="asset-name" placeholder="King Fahd Hospital"
              className="mt-0.5 w-full text-sm border border-gray-300 rounded-md bg-white px-2 py-1.5" />
          </label>
          <label className="text-[11px] font-semibold text-gray-600">Type
            <select value={form.asset_type} onChange={(e) => setForm({ ...form, asset_type: e.target.value })}
              className="mt-0.5 w-full text-sm border border-gray-300 rounded-md bg-white px-2 py-1.5">
              {ASSET_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
          <label className="text-[11px] font-semibold text-gray-600">Criticality
            <select value={form.criticality} onChange={(e) => setForm({ ...form, criticality: e.target.value })}
              data-testid="asset-criticality"
              className="mt-0.5 w-full text-sm border border-gray-300 rounded-md bg-white px-2 py-1.5">
              {[5, 4, 3, 2, 1].map((c) => <option key={c} value={c}>{c}{c === 5 ? ' — critical' : c === 1 ? ' — low' : ''}</option>)}
            </select>
          </label>
          <label className="col-span-2 text-[11px] font-semibold text-gray-600">Zone (or GPS below)
            <select value={form.zone_id} onChange={(e) => setForm({ ...form, zone_id: e.target.value })}
              className="mt-0.5 w-full text-sm border border-gray-300 rounded-md bg-white px-2 py-1.5">
              <option value="">— no zone —</option>
              {zones.map((z) => <option key={z.id} value={z.id}>{z.label || z.zone_name || z.name || z.id}</option>)}
            </select>
          </label>
          <label className="text-[11px] font-semibold text-gray-600">Latitude
            <input value={form.lat} onChange={(e) => setForm({ ...form, lat: e.target.value })}
              placeholder="21.492" className="mt-0.5 w-full text-sm border border-gray-300 rounded-md bg-white px-2 py-1.5" />
          </label>
          <label className="text-[11px] font-semibold text-gray-600">Longitude
            <input value={form.lng} onChange={(e) => setForm({ ...form, lng: e.target.value })}
              placeholder="39.177" className="mt-0.5 w-full text-sm border border-gray-300 rounded-md bg-white px-2 py-1.5" />
          </label>
          <div className="col-span-2 md:col-span-4 flex items-center gap-2">
            <button onClick={save} disabled={saving} data-testid="asset-save"
              className="flex items-center gap-1 rounded-md bg-violet-600 hover:bg-violet-700 text-white text-[11px] font-bold px-3 py-1.5 disabled:opacity-60">
              {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />} Save
            </button>
            <button onClick={() => setForm(null)}
              className="flex items-center gap-1 rounded-md border border-gray-200 bg-white text-gray-600 text-[11px] font-semibold px-3 py-1.5 hover:bg-gray-50">
              <X className="h-3 w-3" /> Cancel
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <p className="text-[12px] text-gray-400 py-2">Loading assets…</p>
      ) : assets.length === 0 && !form ? (
        <p className="text-[12px] text-gray-400 py-2" data-testid="assets-empty">
          No assets yet — the risk engine's Assets factor stays a data gap until you register some.
        </p>
      ) : (
        <div className="divide-y divide-gray-100">
          {assets.map((a) => (
            <div key={a.id} className="flex items-center gap-2 py-1.5" data-testid={`asset-row-${a.id}`}>
              <span className={`inline-flex items-center rounded-full border px-1.5 py-0.5 text-[10px] font-bold ${CRITICALITY_STYLE[a.criticality] || CRITICALITY_STYLE[1]}`}>
                C{a.criticality}
              </span>
              <span className="text-[13px] font-semibold text-gray-800">{a.name}</span>
              <span className="text-[11px] text-gray-400">{a.asset_type}</span>
              {a.is_active === false && <span className="text-[10px] text-gray-400 border border-gray-200 rounded px-1">inactive</span>}
              <span className="ml-auto flex items-center gap-1">
                <button onClick={() => edit(a)} title="Edit"><Pencil className="h-3.5 w-3.5 text-gray-400 hover:text-gray-700" /></button>
                <button onClick={() => remove(a)} title="Delete" data-testid={`asset-delete-${a.id}`}>
                  <Trash2 className="h-3.5 w-3.5 text-gray-400 hover:text-red-600" />
                </button>
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CriticalAssetsCard;
